import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { acoes } from '../lib/api.js';
import { formatHoraSC } from '../lib/tempo.js';

const COR_SEVERIDADE = {
  alta:   { bg: '#fee2e2', cor: '#991b1b', borda: '#dc2626' },
  media:  { bg: '#fef3c7', cor: '#92400e', borda: '#f59e0b' },
  baixa:  { bg: '#dbeafe', cor: '#1e40af', borda: '#3b82f6' },
};

const COR_STATUS = {
  proposta:  { bg: '#fef3c7', cor: '#92400e', label: 'Aguardando' },
  aprovada:  { bg: '#dbeafe', cor: '#1e40af', label: 'Aprovada' },
  executada: { bg: '#dcfce7', cor: '#14532d', label: 'Executada' },
  rejeitada: { bg: '#f3f4f6', cor: '#374151', label: 'Rejeitada' },
  falha:     { bg: '#fee2e2', cor: '#991b1b', label: 'Falha' },
};

const TIPO_TX = {
  reagendar_visita_para_outro_slot:   'Reagendar visita',
  realocar_visita_para_outro_tecnico: 'Realocar técnico',
  cancelar_visita_por_inviabilidade:  'Cancelar visita',
  notificar_atraso_proximo_cliente:   'Notificar atraso cliente',
  enviar_link_tracking_cliente:       'Enviar tracking cliente',
  apenas_observacao:                  'Observação',
};

export default function AcoesPage() {
  const [lista, setLista] = useState([]);
  const [stats, setStats] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('proposta'); // default: pendentes
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => { carregar(); }, [filtroStatus]);

  async function carregar() {
    setCarregando(true);
    try {
      const r = await acoes.list({ status: filtroStatus === 'todas' ? undefined : filtroStatus, limit: 50 });
      setLista(r.data || []);
      try { setStats((await acoes.stats()).data || null); } catch {}
      setErro(null);
    } catch (e) {
      setErro(e.uiMessage || 'Erro ao carregar ações');
    } finally { setCarregando(false); }
  }

  async function disparaTick() {
    setCarregando(true);
    try {
      const r = await acoes.tick();
      // Backend pode retornar HTTP 200 com campo "erro" (ex: créditos esgotados na Anthropic)
      // r tem estrutura: {ts, ms, visitas, veiculos, paradas, propostas_geradas: [], erro?}
      const body = r.data || r;
      const novas = (body.propostas_geradas || []).length;
      if (body.erro) {
        // Detecta erros conhecidos pra mostrar mensagem mais útil
        const m = String(body.erro);
        if (/credit balance|too low|billing/i.test(m)) {
          alert('⚠️ Créditos da Anthropic esgotados.\n\nA IA não consegue gerar novas propostas até a recarga.\nAcesse https://console.anthropic.com/settings/billing pra recarregar.');
        } else {
          alert(`Análise rodou mas a IA não conseguiu gerar propostas:\n\n${m.slice(0, 300)}`);
        }
      } else {
        alert(`✓ Análise concluída em ${body.ms || '?'}ms.\n\n${novas} nova(s) proposta(s) gerada(s).\n\nVisitas analisadas: ${body.visitas || 0}\nVeículos: ${body.veiculos || 0}\nParadas detectadas: ${body.paradas || 0}`);
      }
      carregar();
    } catch (e) {
      alert(`Erro no tick: ${e.uiMessage || e.message}`);
    } finally { setCarregando(false); }
  }

  return (
    <div className="stack">
      {erro && <div className="card error">{erro}</div>}

      <div className="row between">
        <h2 style={{ margin: 0 }}>Ações IA</h2>
        <button className="btn small" onClick={disparaTick} disabled={carregando}>
          ⚡ Disparar análise agora
        </button>
      </div>

      {/* Stats globais */}
      {stats && (
        <div className="card" style={{ padding: '0.6rem 1rem' }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
            <span><strong>{stats.pendentes || 0}</strong> pendentes</span>
            <span><strong>{stats.aprovadas || 0}</strong> aprovadas hoje</span>
            <span><strong>{stats.executadas || 0}</strong> executadas hoje</span>
            <span><strong>{stats.rejeitadas || 0}</strong> rejeitadas hoje</span>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="card" style={{ padding: '0.6rem 1rem' }}>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {['proposta', 'aprovada', 'executada', 'rejeitada', 'todas'].map(s => (
            <Chip key={s} ativo={filtroStatus === s} onClick={() => setFiltroStatus(s)}>
              {s === 'todas' ? 'Todas' : (COR_STATUS[s]?.label || s)}
            </Chip>
          ))}
        </div>
      </div>

      {/* Lista */}
      <div className="stack">
        {lista.length === 0 && !carregando && (
          <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            Nenhuma ação {filtroStatus !== 'todas' ? `com status "${COR_STATUS[filtroStatus]?.label || filtroStatus}"` : ''}.
          </div>
        )}
        {lista.map(a => <CardAcao key={a.id} acao={a} onAcao={carregar} />)}
      </div>
    </div>
  );
}

function CardAcao({ acao, onAcao }) {
  const [executando, setExecutando] = useState(false);
  const sev = COR_SEVERIDADE[acao.severidade] || COR_SEVERIDADE.baixa;
  const st = COR_STATUS[acao.status] || COR_STATUS.proposta;

  async function aprovar() {
    if (!confirm(`Aprovar essa ação?\n\n${acao.motivo}`)) return;
    setExecutando(true);
    try {
      await acoes.aprovar(acao.id, { operador_observacao: 'Aprovado via UI' });
      onAcao();
    } catch (e) {
      alert('Erro: ' + (e.uiMessage || e.message));
    } finally { setExecutando(false); }
  }

  async function rejeitar() {
    const motivo = prompt('Motivo da rejeição (opcional):', '');
    if (motivo === null) return;
    setExecutando(true);
    try {
      await acoes.rejeitar(acao.id, { operador_observacao: motivo || 'Rejeitado via UI' });
      onAcao();
    } catch (e) {
      alert('Erro: ' + (e.uiMessage || e.message));
    } finally { setExecutando(false); }
  }

  return (
    <div className="card" style={{
      padding: '1rem', borderLeft: `4px solid ${sev.borda}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
            <span style={{
              fontSize: '0.7rem', fontWeight: 700,
              padding: '2px 8px', borderRadius: 10,
              background: sev.bg, color: sev.cor,
            }}>
              {acao.severidade?.toUpperCase()}
            </span>
            <span style={{
              fontSize: '0.7rem', fontWeight: 600,
              padding: '2px 8px', borderRadius: 10,
              background: st.bg, color: st.cor,
            }}>
              {st.label}
            </span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              #{acao.id} · {formatHoraSC(acao.dt_criacao)}
            </span>
          </div>

          <h3 style={{ margin: '0 0 0.3rem 0', fontSize: '1rem' }}>
            {TIPO_TX[acao.tipo] || acao.tipo}
            {acao.visita_id && (
              <Link to={`/visitas/${acao.visita_id}`}
                style={{ fontSize: '0.75rem', marginLeft: '0.5rem', textDecoration: 'none', color: '#1e6fc8' }}>
                ↗ Visita #{acao.visita_id}
              </Link>
            )}
          </h3>

          {acao.motivo && (
            <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.4rem' }}>
              <strong>Motivo:</strong> {acao.motivo}
            </div>
          )}

          {acao.payload?.mensagem_sugerida && (
            <div style={{
              fontSize: '0.8rem', background: '#f9fafb', padding: '0.5rem',
              borderRadius: 4, marginTop: '0.3rem', fontStyle: 'italic',
            }}>
              💬 "{acao.payload.mensagem_sugerida}"
            </div>
          )}

          {acao.operador_observacao && (
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.3rem' }}>
              <strong>Decisão do operador:</strong> {acao.operador_observacao}
            </div>
          )}
        </div>

        {acao.status === 'proposta' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 120 }}>
            <button className="btn small" onClick={aprovar} disabled={executando}>
              ✓ Aprovar
            </button>
            <button className="btn small secondary" onClick={rejeitar} disabled={executando}>
              ✗ Rejeitar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ children, ativo, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '0.3rem 0.8rem', borderRadius: 16,
      fontSize: '0.8rem', fontWeight: 500,
      background: ativo ? '#1e6fc8' : '#fff',
      color: ativo ? '#fff' : '#374151',
      border: `1px solid ${ativo ? '#1e6fc8' : 'var(--cinza-borda)'}`,
      cursor: 'pointer',
    }}>{children}</button>
  );
}
